// Stroke Play runner. Pure function — given the round's players, scores,
// and the user-configured Stroke Play options, returns each player's
// net winnings (positive = up, negative = down) across the three
// possible segments: Overall (18), and — when Nassau is on — Front 9
// and Back 9 sub-bets.
//
// Each segment is its own self-contained pot:
//   pot = (participating players) × (segment bet)
//   winners share the pot equally; non-winners lose their stake.
// "Participating players" = players who have at least one scored hole
// in the segment, so an in-progress segment still shows a projected
// payout based on whoever's scored so far.
//
// No state, no side effects, no React. Same shape pattern as the
// Stableford runner so the leaderboard can consume both uniformly.

import { Player, ScoreMap } from "../types";

export type TeamSetup = "1-2v3-4" | "1-3v2-4" | "1-4v2-3";

export type TeamFormat = "aggregate" | "betterBall";

export interface StrokePlayOptions {
  overallBet: number;
  nassau: boolean;
  frontNineBet?: number;
  backNineBet?: number;
  // When `team` is on, every bet (Overall + any active Nassau segments)
  // settles as two-player teams instead of N-way individuals. Team
  // pairing comes from `teamSetup`. Team mode is only honoured when the
  // round has exactly four players; otherwise we silently fall back to
  // individual settlement.
  team?: boolean;
  teamSetup?: TeamSetup;
  // How each team's per-hole score is aggregated:
  //   • "aggregate"  → sum of both members' scores (default)
  //   • "betterBall" → lowest score per hole, summed across the segment
  // Only consulted in team mode; ignored otherwise.
  teamFormat?: TeamFormat;
  // Non-Nassau settlement mode. When `useBetPerPoint` is true and
  // Nassau is off, the runner switches the Overall bucket from a
  // pot-style settlement (overallBet) to head-to-head per-stroke
  // transfers using `betPerPoint`. Nassau ON ignores both — each
  // Nassau segment stays pot-style.
  betPerPoint?: number;
  useBetPerPoint?: boolean;
  // Tournament: entry fee/ante collected up front → pot settlements show gross
  // winnings (winner's full share, $0 for others) with no stake deducted.
  // Off → current net settle-up. Only affects pot-style settlements.
  entryFeePaid?: boolean;
}

// Decode a teamSetup token into the two two-player teams. Order of
// players within a team doesn't affect scoring; we preserve the
// catalog's left-to-right labelling so debug logs stay readable.
// Exported because the leaderboard reuses it to render the team-colour
// dots beside each player's name.
export function teamsFromSetup<T>(
  players: T[],
  setup?: TeamSetup,
): [T[], T[]] {
  const [a, b, c, d] = players;
  if (setup === "1-3v2-4") return [[a, c], [b, d]];
  if (setup === "1-4v2-3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

export interface StrokePlayPayout {
  // Net dollars from each segment (winnings minus stake). 0 when the
  // segment isn't in play or the player didn't participate.
  overall: number;
  front: number;
  back: number;
  total: number;
}

export interface StrokePlayResult {
  payouts: Record<string, StrokePlayPayout>;
  // True when at least one segment has a bet > 0. The leaderboard uses
  // this to decide whether to render the Stroke Play sub-board at all.
  hasAnyBet: boolean;
  // True when Nassau is enabled — the leaderboard surfaces per-segment
  // OUT / IN columns alongside the cumulative total when so.
  nassauActive: boolean;
}

export interface StrokePlayInput {
  players: Player[];
  scores: ScoreMap;
  options: StrokePlayOptions;
}

export function runStrokePlay(input: StrokePlayInput): StrokePlayResult {
  const { players, scores, options } = input;

  const overallBet = Number(options.overallBet) || 0;
  const frontBet = options.nassau ? Number(options.frontNineBet) || 0 : 0;
  const backBet = options.nassau ? Number(options.backNineBet) || 0 : 0;
  // Entry fee collected up front → show gross pot winnings (no stake deducted).
  const feePaid = options.entryFeePaid === true;

  const payouts: Record<string, StrokePlayPayout> = {};
  for (const p of players) {
    payouts[p.id] = { overall: 0, front: 0, back: 0, total: 0 };
  }

  // Team mode applies to every active bet (Overall and the optional
  // Nassau front/back segments). Requires exactly four players so the
  // teamSetup pairing resolves cleanly; any other arrangement falls
  // back to individual settlement.
  const teamMode = !!options.team && players.length === 4;

  // Stroke Play is gross — sums raw gross scores within each segment.
  // No handicap math (the catalog deliberately doesn't expose
  // a handicap mode option for this game).
  const segmentScore = (
    pid: string,
    inSegment: (hole: number) => boolean,
  ): { score: number; hasAny: boolean } => {
    const ps = scores[pid] || {};
    let score = 0;
    let hasAny = false;
    Object.entries(ps).forEach(([holeStr, sc]) => {
      if (typeof sc.gross !== "number") return;
      const h = parseInt(holeStr, 10);
      if (inSegment(h)) {
        score += sc.gross;
        hasAny = true;
      }
    });
    return { score, hasAny };
  };

  // Per-point individual settlement (Nassau off, useBetPerPoint on).
  // For every ordered pair of scored players, the LOWER score wins:
  //   payouts[a] += (b.score − a.score) × bet
  // So if A shoots 72 and B shoots 75 at $1/stroke, A nets +$3 from B.
  // The table sums to zero across all opponents and a tie produces
  // no movement.
  const settlePerPointIndividual = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const segScores: Array<{ id: string; score: number }> = [];
    for (const p of players) {
      const s = segmentScore(p.id, inSegment);
      if (s.hasAny) segScores.push({ id: p.id, score: s.score });
    }
    if (segScores.length < 2) return;
    for (const a of segScores) {
      for (const b of segScores) {
        if (a.id === b.id) continue;
        // Lower strokes wins → opponent's score minus our score.
        payouts[a.id][bucket] += (b.score - a.score) * bet;
      }
    }
  };

  // Per-point team settlement (Nassau off, useBetPerPoint on, team
  // mode on): each team's segment total is compared once and every
  // teammate on a side picks up (theirTeam − myTeam) × bet (lower
  // wins). Both teammates land on the same net; the four-player
  // table stays zero-sum.
  const settlePerPointTeam = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const teams = teamsFromSetup(players, options.teamSetup);
    const format: TeamFormat =
      options.teamFormat === "betterBall" ? "betterBall" : "aggregate";
    const teamScores = teams.map((team) => {
      const s = teamSegmentScore(team, inSegment, format);
      return { team, score: s.score, hasAny: s.hasAny };
    });
    const playing = teamScores.filter((t) => t.hasAny);
    if (playing.length < 2) return;
    const [a, b] = playing;
    const aNet = (b.score - a.score) * bet;
    const bNet = (a.score - b.score) * bet;
    for (const p of a.team) payouts[p.id][bucket] += aNet;
    for (const p of b.team) payouts[p.id][bucket] += bNet;
  };

  // Settle one segment, individual mode. Player participates if they
  // have at least one scored hole in the segment's hole range. Tied
  // lowest scores split the pot evenly; everyone else loses their stake.
  const settleIndividual = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const segScores: Array<{ id: string; score: number }> = [];
    for (const p of players) {
      const s = segmentScore(p.id, inSegment);
      if (s.hasAny) segScores.push({ id: p.id, score: s.score });
    }
    if (segScores.length === 0) return;
    const minScore = Math.min(...segScores.map((s) => s.score));
    const winners = segScores.filter((s) => s.score === minScore);
    const pot = segScores.length * bet;
    const winnerShare = pot / winners.length;
    for (const sp of segScores) {
      const isWinner = sp.score === minScore;
      // Fee paid up front → gross (winner: share, others: 0); otherwise net.
      payouts[sp.id][bucket] += isWinner
        ? feePaid
          ? winnerShare
          : winnerShare - bet
        : feePaid
          ? 0
          : -bet;
    }
  };

  // Per-hole team aggregator. Walks each hole touched by either
  // teammate, in-segment only, and returns the team's segment score
  // under the chosen team format:
  //   • aggregate  → sum of both teammates' per-hole scores
  //   • betterBall → sum of the lower of the two per-hole scores
  // A hole with only one teammate scored counts that lone score (so
  // live leaderboards stay sensible mid-round). Returns `hasAny` so
  // callers can detect a non-participating team.
  const teamSegmentScore = (
    team: Player[],
    inSegment: (hole: number) => boolean,
    format: TeamFormat,
  ): { score: number; hasAny: boolean } => {
    const holes = new Set<number>();
    for (const p of team) {
      const ps = scores[p.id] || {};
      for (const k of Object.keys(ps)) {
        const sc = ps[k as unknown as number];
        if (sc && typeof sc.gross === "number") holes.add(parseInt(k, 10));
      }
    }
    let score = 0;
    let hasAny = false;
    for (const h of holes) {
      if (!inSegment(h)) continue;
      const holeScores: number[] = [];
      for (const p of team) {
        const sc = (scores[p.id] || {})[h];
        if (sc && typeof sc.gross === "number") holeScores.push(sc.gross);
      }
      if (holeScores.length === 0) continue;
      hasAny = true;
      if (format === "betterBall") score += Math.min(...holeScores);
      else score += holeScores.reduce((a, b) => a + b, 0);
    }
    return { score, hasAny };
  };

  // Settle one segment, team mode (2v2). Each player still puts the
  // same bet into the pot; team score is computed via teamSegmentScore
  // under the configured teamFormat; winning team's members each take
  // an equal slice of the total pot. Tied team scores split evenly
  // across both teams (each player nets out). A team only participates
  // when at least one of its members has scored in the segment.
  const settleTeam = (
    inSegment: (hole: number) => boolean,
    bet: number,
    bucket: "overall" | "front" | "back",
  ) => {
    if (bet <= 0) return;
    const teams = teamsFromSetup(players, options.teamSetup);
    const format: TeamFormat =
      options.teamFormat === "betterBall" ? "betterBall" : "aggregate";
    const teamScores = teams.map((team) => {
      const s = teamSegmentScore(team, inSegment, format);
      return { team, score: s.score, hasAny: s.hasAny };
    });
    const playing = teamScores.filter((t) => t.hasAny);
    if (playing.length < 2) return;
    const minScore = Math.min(...playing.map((t) => t.score));
    const winningTeams = playing.filter((t) => t.score === minScore);
    const totalPlayers = playing.reduce((sum, t) => sum + t.team.length, 0);
    const pot = totalPlayers * bet;
    const winnerPlayerCount = winningTeams.reduce(
      (sum, t) => sum + t.team.length,
      0,
    );
    const perWinnerShare = pot / winnerPlayerCount;
    for (const t of playing) {
      const won = t.score === minScore;
      for (const p of t.team) {
        payouts[p.id][bucket] += won
          ? feePaid
            ? perWinnerShare
            : perWinnerShare - bet
          : feePaid
            ? 0
            : -bet;
      }
    }
  };

  const settle = teamMode ? settleTeam : settleIndividual;

  // Non-Nassau settlement of the Overall bucket branches on the
  // user-facing toggle (same shape as Stableford):
  //   • useBetPerPoint = false → pot-style with overallBet (default)
  //   • useBetPerPoint = true  → per-stroke with betPerPoint
  // Nassau ON ignores the toggle entirely and runs all three segments
  // through the pot-style settler — the long-standing three-pot
  // Nassau format.
  if (!options.nassau) {
    if (options.useBetPerPoint === true) {
      const ppBet = Number(options.betPerPoint) || 0;
      const settlePerPoint = teamMode
        ? settlePerPointTeam
        : settlePerPointIndividual;
      settlePerPoint((h) => h >= 1 && h <= 18, ppBet, "overall");
    } else {
      settle((h) => h >= 1 && h <= 18, overallBet, "overall");
    }
  } else {
    settle((h) => h >= 1 && h <= 18, overallBet, "overall");
    settle((h) => h >= 1 && h <= 9, frontBet, "front");
    settle((h) => h >= 10 && h <= 18, backBet, "back");
  }

  for (const p of players) {
    const pay = payouts[p.id];
    pay.total = pay.overall + pay.front + pay.back;
  }

  const ppBetValue = Number(options.betPerPoint) || 0;
  const nonNassauActive =
    options.useBetPerPoint === true ? ppBetValue > 0 : overallBet > 0;
  const hasAnyBet = options.nassau
    ? overallBet > 0 || frontBet > 0 || backBet > 0
    : nonNassauActive;

  return {
    payouts,
    hasAnyBet,
    nassauActive: !!options.nassau,
  };
}
